import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, User, Trash2, Edit, Key } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HREmployees() {
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [resetPinEmployee, setResetPinEmployee] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [formData, setFormData] = useState({ employee_id: '', name: '', pin: '', department: '', field_tracking: false, assigned_locations: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/hr/login');
      return;
    }
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'hr') {
      navigate('/hr/login');
      return;
    }
    setUser(parsedUser);
    fetchEmployees();
    fetchLocations();
  }, [navigate]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.name || !formData.pin) {
      toast.error('Please fill all fields');
      return;
    }
    if (formData.pin.length < 4 || formData.pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees`, {
        ...formData,
        department: formData.department.trim() || null,
        assigned_location_ids: formData.assigned_locations
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee added successfully');
      setFormData({ employee_id: '', name: '', pin: '', department: '', field_tracking: false, assigned_locations: [] });
      setShowAddForm(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleResetPin = async () => {
    if (!newPin || newPin.length < 4 || newPin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/employees/${resetPinEmployee.id}/reset-pin?new_pin=${newPin}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PIN reset successfully');
      setResetPinEmployee(null);
      setNewPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLocations = async (employeeId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/employees/${employeeId}/locations`, editingEmployee.assigned_locations || [], {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.put(`${API}/employees/${employeeId}`, {
        department: editingEmployee.department.trim() || null,
        active: editingEmployee.active,
        field_tracking: editingEmployee.field_tracking
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee updated successfully');
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update locations');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/hr/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white border-b-2 border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <img 
              src="/anmol-logo.jpg" 
              alt="Advanced Anmol Metcomp Pvt Ltd" 
              className="h-12"
            />
            <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium" data-testid="employees-logout-button">
              Logout
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="employees-title">Employees</h1>
              <p className="text-sm text-zinc-500 font-medium mt-1">{user.name}</p>
            </div>
          </div>

          <div className="flex gap-4 mt-6 flex-wrap">
            <button
              onClick={() => navigate('/hr/dashboard')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-dashboard-from-employees"
            >
              Attendance
            </button>
            <button
              onClick={() => navigate('/hr/employees')}
              className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm"
              data-testid="nav-employees-active"
            >
              Employees
            </button>
            <button
              onClick={() => navigate('/hr/locations')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-locations-from-employees"
            >
              Locations
            </button>
            <button
              onClick={() => navigate('/hr/reports')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-reports-from-employees"
            >
              Reports
            </button>
            <button
              onClick={() => navigate('/hr/notifications')}
              className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              data-testid="nav-notifications-from-employees"
            >
              Notifications
            </button>
            <button onClick={() => navigate('/hr/tracking')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-tracking-from-employees">Tracking</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="h-12 px-6 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-800 active:scale-95 transition-all flex items-center gap-2"
            data-testid="add-employee-button"
          >
            <Plus size={20} />
            Add New Employee
          </button>
        </div>

        {showAddForm && (
          <div className="bg-white border-2 border-zinc-100 rounded-lg p-6 mb-6">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-zinc-900 mb-4" data-testid="add-employee-form-title">New Employee</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Employee ID</label>
                <input
                  type="text"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                  placeholder="e.g., EMP001"
                  data-testid="new-employee-id-input"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                  placeholder="e.g., John Doe"
                  data-testid="new-employee-name-input"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Department (optional)</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                  placeholder="e.g., Field Service"
                  maxLength={100}
                  data-testid="new-employee-department-input"
                  disabled={loading}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.field_tracking}
                  onChange={(e) => setFormData({ ...formData, field_tracking: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="new-employee-field-checkbox"
                  disabled={loading}
                />
                <span className="text-sm font-medium text-zinc-900">Field employee (uses the Field Tracking Android app)</span>
              </label>
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">PIN (4-6 digits)</label>
                <input
                  type="password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                  placeholder="Enter 4-6 digit PIN"
                  maxLength={6}
                  data-testid="new-employee-pin-input"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Assign Approved Locations</label>
                <div className="bg-zinc-50 border-2 border-zinc-200 rounded-sm p-4 max-h-48 overflow-y-auto">
                  {locations.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-2">No locations available. Add locations first.</p>
                  ) : (
                    <div className="space-y-2">
                      {locations.map(location => (
                        <label key={location.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.assigned_locations.includes(location.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, assigned_locations: [...formData.assigned_locations, location.id] });
                              } else {
                                setFormData({ ...formData, assigned_locations: formData.assigned_locations.filter(id => id !== location.id) });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-medium text-zinc-900">{location.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-zinc-500 mt-2">{formData.assigned_locations.length} location(s) selected</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="h-12 px-6 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50"
                  data-testid="submit-employee-button"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Employee'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="h-12 px-6 bg-white border-2 border-zinc-200 text-zinc-900 font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900 active:scale-95 transition-all"
                  data-testid="cancel-add-employee-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee, idx) => (
            <div key={employee.id} className="bg-white border-2 border-zinc-100 rounded-lg p-6" data-testid={`employee-card-${idx}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-zinc-900 truncate" data-testid={`employee-name-${idx}`}>{employee.name}</h3>
                  <p className="font-mono text-sm text-zinc-500" data-testid={`employee-id-${idx}`}>ID: {employee.employee_id}</p>
                  {employee.department && (
                    <p className="text-xs text-zinc-500 mt-1" data-testid={`employee-department-${idx}`}>{employee.department}</p>
                  )}
                  {employee.field_tracking && (
                    <p className="text-xs font-bold text-green-700 uppercase mt-1">Field employee</p>
                  )}
                  {employee.active === false && (
                    <p className="text-xs font-bold text-red-700 uppercase mt-1">Inactive</p>
                  )}
                  {employee.assigned_location_ids && employee.assigned_location_ids.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">{employee.assigned_location_ids.length} location(s) assigned</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setEditingEmployee({ ...employee, assigned_locations: employee.assigned_location_ids || [], department: employee.department || '', active: employee.active !== false, field_tracking: !!employee.field_tracking })}
                  className="flex-1 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 text-sm font-bold uppercase rounded flex items-center justify-center gap-2"
                  data-testid={`edit-employee-${idx}`}
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  onClick={() => setResetPinEmployee(employee)}
                  className="flex-1 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-900 text-sm font-bold uppercase rounded flex items-center justify-center gap-2"
                  data-testid={`reset-pin-${idx}`}
                >
                  <Key size={16} />
                  PIN
                </button>
                <button
                  onClick={() => handleDelete(employee.id, employee.name)}
                  className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-900 rounded flex items-center justify-center"
                  data-testid={`delete-employee-${idx}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {employees.length === 0 && (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <Users size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium">No employees added yet</p>
            <p className="text-sm text-zinc-400 mt-1">Add employees to start tracking attendance</p>
          </div>
        )}
      </div>

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-heading text-xl font-bold uppercase text-zinc-900 mb-4">Edit Employee</h3>
            <p className="text-sm text-zinc-600 mb-4">
              <strong>{editingEmployee.name}</strong> (ID: {editingEmployee.employee_id})
            </p>
            <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Department</label>
            <input
              type="text"
              value={editingEmployee.department}
              onChange={(e) => setEditingEmployee({ ...editingEmployee, department: e.target.value })}
              className="w-full h-12 px-4 mb-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none"
              maxLength={100}
              data-testid="edit-employee-department-input"
            />
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={editingEmployee.active}
                onChange={(e) => setEditingEmployee({ ...editingEmployee, active: e.target.checked })}
                className="w-4 h-4"
                data-testid="edit-employee-active-checkbox"
              />
              <span className="text-sm font-medium text-zinc-900">Active (unticking logs the employee out of the field app)</span>
            </label>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={editingEmployee.field_tracking}
                onChange={(e) => setEditingEmployee({ ...editingEmployee, field_tracking: e.target.checked })}
                className="w-4 h-4"
                data-testid="edit-employee-field-checkbox"
              />
              <span className="text-sm font-medium text-zinc-900">Field employee (can use the Field Tracking app; shown in Tracking)</span>
            </label>
            <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Assigned Locations</label>
            <div className="bg-zinc-50 border-2 border-zinc-200 rounded-sm p-4 max-h-64 overflow-y-auto mb-4">
              {locations.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-2">No locations available</p>
              ) : (
                <div className="space-y-2">
                  {locations.map(location => (
                    <label key={location.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                      <input
                        type="checkbox"
                        checked={editingEmployee.assigned_locations?.includes(location.id)}
                        onChange={(e) => {
                          const newLocations = e.target.checked
                            ? [...(editingEmployee.assigned_locations || []), location.id]
                            : (editingEmployee.assigned_locations || []).filter(id => id !== location.id);
                          setEditingEmployee({ ...editingEmployee, assigned_locations: newLocations });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-zinc-900">{location.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleUpdateLocations(editingEmployee.id)}
                disabled={loading}
                className="flex-1 h-12 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditingEmployee(null)}
                className="flex-1 h-12 bg-white border-2 border-zinc-200 text-zinc-900 font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset PIN Modal */}
      {resetPinEmployee && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-heading text-xl font-bold uppercase text-zinc-900 mb-4">Reset PIN</h3>
            <p className="text-sm text-zinc-600 mb-4">
              <strong>{resetPinEmployee.name}</strong> (ID: {resetPinEmployee.employee_id})
            </p>
            <div className="mb-4">
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">New PIN (4-6 digits)</label>
              <input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors"
                placeholder="Enter new PIN"
                maxLength={6}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleResetPin}
                disabled={loading}
                className="flex-1 h-12 bg-blue-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset PIN'}
              </button>
              <button
                onClick={() => { setResetPinEmployee(null); setNewPin(''); }}
                className="flex-1 h-12 bg-white border-2 border-zinc-200 text-zinc-900 font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
